import React, {useEffect, useRef, useState} from 'react';
import './ReactionsPanel.css';
import {useDispatch, useSelector} from 'react-redux';
import {selectGameType, selectPlayers} from '../../redux/reducers/sharedStateReducer/selectors';
import {selectLobbyId, selectUserId} from '../../redux/reducers/privateStateReducer/selectors';
import {GAME_TYPES} from '../../constants/game';
import {REACTION_RECIPIENT_ALL, REACTION_TYPES} from '../../configs/reactions';
import ReactionIcon from '../ReactionIcon/ReactionIcon';
import {getPlayerHexColor} from '../../utils/players/getPlayerHexColor';
import FactionSkinIcon from '../RankSkinFrame/FactionSkinIcon';
import {enqueueOwnReaction} from '../../redux/actions';
import {getNetworkSocket} from '../../utils/networkSocket';
import {
  getReactionBlockRemainingMs,
  getReactionBlockRemainingSeconds,
  registerReactionSent
} from '../../utils/reactions/reactionCooldown';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {addClassNameIfTrue} from '../../utils/other/addClassNameIfTrue';
import {WAR_COLOR} from '../../styles/constants';
import {scheduleBotReply} from '../../utils/reactions/scheduleBotReply';
import {sendBotReaction} from '../../utils/reactions/sendBotReaction';
import {getReactionSenderId} from '../../utils/reactions/getReactionSenderId';
import {GAME_BUTTON_SIZES, GameButton} from '../gameUi/GameButton';
import {GamePanel} from '../gameUi/GamePanel';
import {EngravedIcon} from '../gameUi/EngravedIcon';
import {useCollapseOnCornerPanelRequest} from '../../hooks/visuals/useCornerPanels';

// Панель отправки реакций (openspec/changes/reaction-messages): свернутая
// кнопка chat.png в левом нижнем углу header-above-controls-container, по
// клику — двухшаговая панель (адресат -> тип реакции). Только сетевые матчи.
// onExpandedChange поднимает флаг в Footer: развернутая панель требует
// z-index выше кнопок боковых панелей, а он задается классом на .app-footer
// (сам футер — stacking context с z-index 100)
const ReactionsPanel = ({onExpandedChange, isCollapseRequested = false}) => {
  const dispatch = useDispatch();
  const gameType = useSelector(selectGameType);
  const players = useSelector(selectPlayers);
  const userId = useSelector(selectUserId);
  const lobbyId = useSelector(selectLobbyId);

  // Админ, перехвативший управление ботом (см. getActingPlayer), пишет от
  // имени этого бота: своей игровой личности у зрителя нет, и реакцию «от
  // зрителя» сервер не примет (fromPlayerId — см. SEND_REACTION на сервере)
  const senderId = getReactionSenderId(players, userId);
  const isSendingAsControlledBot = Boolean(senderId) && senderId !== userId;

  const recipients = players.filter((player) => player.id !== senderId);
  // Матч 1 на 1: единственный получатель кроме себя — адресно ему и «всем»
  // это один и тот же набор получателей, выбор адресата не несет смысла.
  // Шаг 1 пропускается: панель сразу открывается на шаге 2 с адресатом 'all'
  const isTwoPlayerMatch = recipients.length === 1;

  const [isExpanded, setExpanded] = useState(false);
  // null = шаг 1 (выбор адресата); playerId | 'all' = шаг 2 (выбор реакции)
  const [selectedRecipientId, setSelectedRecipientId] = useState(isTwoPlayerMatch ? REACTION_RECIPIENT_ALL : null);
  // {key, durationMs} пока идет кулдаун отправки, иначе null. key меняется
  // при каждом (пере)запуске — заставляет React перемонтировать полоску и
  // перезапустить CSS-анимацию с актуальной оставшейся длительностью
  const [cooldownBar, setCooldownBar] = useState(null);
  // scheduleBotReply (и вся ее рекурсивная цепочка ответов) живет вне
  // React-состояния 10-30 секунд за раз, панель может успеть свернуться и
  // развернуться заново — единственное, что важно отследить, это истинное
  // размонтирование (выход из матча), чтобы цепочка не продолжала слать
  // реакции за уже покинутый матч
  const isUnmountedRef = useRef(false);

  const isGridVisible = isExpanded && selectedRecipientId !== null;

  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  // Синхронизация при каждом показе грида (открытие панели, переход со
  // 2-плеерного авто-шага 2, возврат из шага 1) — а не при каждом рендере:
  // подхватывает кулдаун, оставшийся с предыдущей отправки, если панель
  // была свернута и снова открыта до его истечения
  useEffect(() => {
    if (!isGridVisible) {
      return;
    }
    const remainingMs = getReactionBlockRemainingMs();
    setCooldownBar(remainingMs > 0 ? {key: Date.now(), durationMs: remainingMs} : null);
  }, [isGridVisible]);

  // Развернули другую панель угла — реакции сворачиваются (адресат
  // сбрасывается, как при обычном сворачивании)
  useCollapseOnCornerPanelRequest(isCollapseRequested, isExpanded, () => {
    setExpanded(false);
    onExpandedChange?.(false);
    setSelectedRecipientId(isTwoPlayerMatch ? REACTION_RECIPIENT_ALL : null);
  });

  // Нет отправителя — админ перехватил явного бота, за которого реакции не
  // шлются (см. getReactionSenderId)
  if (gameType !== GAME_TYPES.ONLINE || !senderId) {
    return null;
  }

  const selectedRecipient = selectedRecipientId !== null && selectedRecipientId !== REACTION_RECIPIENT_ALL
    ? recipients.find((player) => player.id === selectedRecipientId)
    : null;

  const setExpandedAndNotify = (nextExpanded) => {
    setExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  const collapseAndReset = () => {
    setExpandedAndNotify(false);
    setSelectedRecipientId(isTwoPlayerMatch ? REACTION_RECIPIENT_ALL : null);
  };

  const onExpandToggleClick = () => {
    REGISTRY_SINGLETON.getSoundManager().playVisualTogglesExpand();
    if (isExpanded) {
      collapseAndReset();
    } else {
      setExpandedAndNotify(true);
    }
  };

  const onReactionClick = (reactionTypeId) => {
    // Кулдаун уже виден в самой панели (полоска + приглушенные иконки) —
    // клик по реакции во время блока просто ничего не делает, без
    // error-тоста и без сворачивания панели
    if (getReactionBlockRemainingSeconds() > 0) {
      return;
    }

    const ws = getNetworkSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (isSendingAsControlledBot) {
        sendBotReaction(lobbyId, senderId, selectedRecipientId, reactionTypeId);
      } else {
        ws.send(JSON.stringify({
          type: 'SEND_REACTION',
          payload: {lobbyId, toPlayerId: selectedRecipientId, reactionType: reactionTypeId},
        }));
      }
      scheduleBotReply(lobbyId, selectedRecipientId, senderId, () => isUnmountedRef.current);
    }
    // Свою реакцию отправитель ставит себе сам — сервер ему эхо не шлет. Так и
    // у админа за перехваченного бота: его реакция видна ему сразу и не
    // скрывается, даже если этого бота он заглушил до перехвата
    dispatch(enqueueOwnReaction(senderId, selectedRecipientId, reactionTypeId));
    registerReactionSent();
    collapseAndReset();
  };

  const renderRecipientLabel = (player) => (
    <>
      <FactionSkinIcon
        imgClassName={'player-info_faction-icon reactions-panel_recipient-faction-icon'}
        factionId={player.factionId}
        skinId={player.selectedSkin}
        alt={player.factionId}
      />
      {/* Имя — светлыми чернилами листа, цвет игрока — подчеркиванием: темные
          цвета (красный, синий) на темном базальте не читались бы */}
      <span
        className={'reactions-panel_recipient-name'}
        style={{'--reactions-recipient-color': getPlayerHexColor(player.color)}}
      >
        {player.name}
      </span>
    </>
  );

  // .reactions-panel — якорь фиксированного размера кнопки-переключателя,
  // не зависящий от isExpanded (см. ReactionsPanel.css): разворачиваясь,
  // .reactions-panel-expanded ложится поверх него через position: absolute
  // и не раздвигает соседей по header-above-controls-container
  return (
    <div className={`reactions-panel ${addClassNameIfTrue(!isExpanded, 'reactions-panel-collapsed')}`}>
      {!isExpanded ? (
        <GameButton
          size={GAME_BUTTON_SIZES.ICON}
          onClick={onExpandToggleClick}
          className={'reactions-panel_toggle-button'}
          title={'Реакции'}
        >
          <EngravedIcon src={'/img/icons/chat.png'} size={24} alt={'reactions'}/>
        </GameButton>
      ) : (
        // Развернутая панель — нейтральный лист базальта (дизайн-система матча)
        <GamePanel shadow={true} className={'reactions-panel-expanded'} data-toasts-avoid={true}>
          <div className={'reactions-panel_body'}>
            <div className={'reactions-panel_side'}>
              <EngravedIcon
                className={'reactions-panel_side-chat-icon'}
                src={'/img/icons/chat.png'}
                size={25}
                alt={'reactions'}
              />
              <button
                onClick={onExpandToggleClick}
                className={'footer-heading-btn reactions-panel_collapse-button'}
                title={'Свернуть'}
              >
                <EngravedIcon src={'/img/icons/expand.png'} size={25} alt={'collapse'}/>
              </button>
            </div>

            <div className={'reactions-panel_content'}>
              {selectedRecipientId === null ? (
                <>
                  <div className={'reactions-panel_title'}>Кому отправить:</div>
                  <div className={'reactions-panel_recipients'}>
                    {recipients.map((player) => (
                      <button
                        key={player.id}
                        className={'reactions-panel_recipient-option'}
                        onClick={() => setSelectedRecipientId(player.id)}
                      >
                        {renderRecipientLabel(player)}
                      </button>
                    ))}
                    <button
                      className={'reactions-panel_recipient-option reactions-panel_recipient-option-all'}
                      onClick={() => setSelectedRecipientId(REACTION_RECIPIENT_ALL)}
                    >
                      Всем
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    className={`reactions-panel_title reactions-panel_recipient-title ${addClassNameIfTrue(selectedRecipient, 'reactions-panel_recipient-title_to-player')}`}
                  >
                    {selectedRecipient ? renderRecipientLabel(selectedRecipient) : 'Всем'}:
                  </div>
                  <div className={'reactions-panel_grid'}>
                    {REACTION_TYPES.map((reaction) => (
                      <button
                        key={reaction.id}
                        className={`footer-heading-btn reactions-panel_reaction-button ${
                          addClassNameIfTrue(cooldownBar !== null, 'reactions-panel_reaction-button-blocked')
                        }`}
                        title={reaction.title}
                        onClick={() => onReactionClick(reaction.id)}
                      >
                        <ReactionIcon reaction={reaction}/>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {cooldownBar && (
              <div className={'reactions-panel_cooldown-track'}>
                <div
                  key={cooldownBar.key}
                  className={'reactions-panel_cooldown-bar'}
                  style={{animationDuration: `${cooldownBar.durationMs}ms`, background: WAR_COLOR}}
                  onAnimationEnd={() => setCooldownBar(null)}
                />
              </div>
            )}
          </div>
        </GamePanel>
      )}
    </div>
  );
};

export default ReactionsPanel;
